"use client";
import React, { useState, useEffect } from "react";
import { GrAnnounce } from "react-icons/gr";
import { FaCamera, FaTimes } from "react-icons/fa";
import * as Dialog from "@radix-ui/react-dialog";
import html2canvas from "html2canvas";
import { submitBugReport } from "@/data/noc/submitBugReport";
import { BUG_REPORT_CATEGORIES, CATEGORIES_WITHOUT_DESCRIPTION_REQUIRED } from "@/data/noc/bugReportCategories";
import { addDatadogAction, startDatadogSessionReplay, getDatadogSessionInfo } from "@/utils/datadog";
import locationsGetV2 from "@/data/calls/location/locationsGetV2";

interface ZohoBugReportButtonProps {
  setReportSent: (sent: boolean) => void;
  locationName?: string;
  locationId?: number;
}

interface BugReportData {
  email: string;
  name: string;
  category: string;
  description: string;
  screenshot?: string;
  aipxLocationName: string;
  aipxLocationId: number;
  browserInfo: {
    userAgent: string;
    url: string;
    pageTitle: string;
    timestamp: string;
  };
}

const ZohoBugReportButton = ({ setReportSent, locationName, locationId }: ZohoBugReportButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [datadogSessionId, setDatadogSessionId] = useState<string | null>(null);
  const [datadogReplayUrl, setDatadogReplayUrl] = useState<string | null>(null);
  const [submissionResult, setSubmissionResult] = useState<{
    success: boolean;
    message: string;
    ticketId?: string;
    rateLimitInfo?: {
      remaining: number;
      retryAfter?: number;
    };
  } | null>(null);
  const [formData, setFormData] = useState<BugReportData>({
    email: "",
    name: "",
    category: "",
    description: "",
    aipxLocationName: "",
    aipxLocationId: 0,
    browserInfo: {
      userAgent: navigator.userAgent,
      url: window.location.href,
      pageTitle: document.title,
      timestamp: new Date().toISOString()
    }
  });
  const [locationList, setLocationList] = useState<any[]>([]);
  const fetchLocations = async () => {
    try {
      const currentLocations = await locationsGetV2({ limit: 55 });
      const rows = Array.isArray(currentLocations?.rows) ? currentLocations.rows : [];
      const validRows = rows.filter(row => row?.id && row?.name);

      const list = validRows.map(({ name, address, id }) => ({
        id: String(id),
        name: String(name),
        address: address ? String(address) : ""
      }));

      setLocationList(list);
    } catch (error) {
      console.error("Error when fetching location data:", error);
      setLocationList([]);
    }
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLocations();
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  // Auto-populate user data from localStorage
  React.useEffect(() => {
    const userData = localStorage.getItem("@BLUE-user");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setFormData(prev => ({
          ...prev,
          email: user.email || "",
          name: `${user.firstName || ""} ${user.lastName || ""}`.trim()
        }));
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleInputChange = (field: keyof BugReportData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const captureScreenshot = async () => {
    try {
      // Find and hide all dialog-related elements more aggressively
      const selectors = [
        '[data-radix-dialog-content]',
        '[data-radix-dialog-overlay]',
        '[role="dialog"]',
        '.fixed.right-0.top-0', // Our specific panel class
        'div[style*="position: fixed"][style*="right: 0"]' // Any fixed right panel
      ];

      const elementsToHide: HTMLElement[] = [];
      const originalStyles: { element: HTMLElement; display: string; visibility: string; opacity: string }[] = [];

      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => {
          const htmlElement = element as HTMLElement;
          if (htmlElement && !elementsToHide.includes(htmlElement)) {
            elementsToHide.push(htmlElement);
            originalStyles.push({
              element: htmlElement,
              display: htmlElement.style.display,
              visibility: htmlElement.style.visibility,
              opacity: htmlElement.style.opacity
            });
            htmlElement.style.display = 'none';
            htmlElement.style.visibility = 'hidden';
            htmlElement.style.opacity = '0';
          }
        });
      });

      // Also hide any elements with z-index higher than 40 (our panel has z-50)
      const allElements = document.querySelectorAll('*');
      allElements.forEach(element => {
        const htmlElement = element as HTMLElement;
        const computedStyle = window.getComputedStyle(htmlElement);
        const zIndex = parseInt(computedStyle.zIndex);
        if (zIndex >= 50 && htmlElement.style.position === 'fixed') {
          if (!elementsToHide.includes(htmlElement)) {
            elementsToHide.push(htmlElement);
            originalStyles.push({
              element: htmlElement,
              display: htmlElement.style.display,
              visibility: htmlElement.style.visibility,
              opacity: htmlElement.style.opacity
            });
            htmlElement.style.display = 'none';
            htmlElement.style.visibility = 'hidden';
            htmlElement.style.opacity = '0';
          }
        }
      });

      // Longer delay to ensure all elements are hidden
      await new Promise(resolve => setTimeout(resolve, 200));

      // Capture the full page content
      const canvas = await html2canvas(document.documentElement, {
        useCORS: true,
        allowTaint: true,
        scale: 0.5,
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: 0,
        scrollY: 0,
        ignoreElements: (element: Element) => {
          // Additional safety check to ignore any remaining dialog elements
          // Cast to HTMLElement to access the dataset property
          const htmlElement = element as HTMLElement;
          return htmlElement.dataset?.radixDialogContent !== undefined || htmlElement.dataset?.radixDialogOverlay !== undefined || element.getAttribute("role") === "dialog";
        }
      });

      // Restore all elements
      originalStyles.forEach(({ element, display, visibility, opacity }) => {
        element.style.display = display;
        element.style.visibility = visibility;
        element.style.opacity = opacity;
      });

      const base64 = canvas.toDataURL("image/png");
      setScreenshot(base64);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      alert("Failed to capture screenshot. Please try again.");
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.category) {
      alert("Please select a category.");
      return;
    }
    const descriptionRequired = !CATEGORIES_WITHOUT_DESCRIPTION_REQUIRED.includes(formData.category as any);
    if (descriptionRequired && !formData.description.trim()) {
      alert("Please provide a description of the issue.");
      return;
    }

    setIsSubmitting(true);
    setSubmissionResult(null);
    const currentLocIdString = formData.aipxLocationId;

    const selectedLocation = locationList.find(loc => loc.id === currentLocIdString);
    const currentLocName = selectedLocation ? String(selectedLocation.name) : "";
    const finalLocationId = currentLocIdString ? Number(currentLocIdString) : undefined;

    try {
      const reportData = {
        ...formData,
        screenshot: screenshot || undefined,
        datadogSessionId: datadogSessionId,
        datadogReplayUrl: datadogReplayUrl,
        locationName: locationName,
        locationId: locationId,
        aipxLocationId: finalLocationId,
        aipxLocationName: currentLocName
      };

      const response = await submitBugReport(reportData);

      // Track bug report submission to Datadog RUM — include session ID so
      // investigators can jump from this action event directly to the replay.

      addDatadogAction('bug_report_submitted', {
        ticketId: response.ticketId,
        hasScreenshot: !!screenshot,
        category: formData.category,
        descriptionLength: formData.description.length,
        userEmail: formData.email,
        userName: formData.name,
        locationName: locationName,
        locationId: locationId,
        pageUrl: formData.browserInfo.url,
        pageTitle: formData.browserInfo.pageTitle,
        userAgent: formData.browserInfo.userAgent,
        timestamp: formData.browserInfo.timestamp,
        datadogSessionId: datadogSessionId,
        datadogReplayUrl: datadogReplayUrl,
      });

      // Handle successful submission
      setSubmissionResult({
        success: true,
        message: response.message || "Bug report submitted successfully!",
        ticketId: response.ticketId,
        rateLimitInfo: response.rateLimitInfo
      });

      // Auto-close after 3 seconds on success
      setTimeout(() => {
        setReportSent(true);
        setIsOpen(false);
        setScreenshot(null);
        setFormData(prev => ({
          ...prev,
          category: "",
          description: "",
        }));
        setSubmissionResult(null);
      }, 3000);
      
    } catch (error: any) {
      console.error("Error submitting bug report:", error);

      // Extract rate limit info from error response if available
      const rateLimitInfo = error.response?.data?.rateLimitInfo;

      // Track failed bug report submission to Datadog RUM
      addDatadogAction('bug_report_failed', {
        errorMessage: error.response?.data?.message || error.message || "Unknown error",
        hasScreenshot: !!screenshot,
        category: formData.category,
        descriptionLength: formData.description.length,
        userEmail: formData.email,
        userName: formData.name,
        locationName: locationName,
        locationId: locationId,
        pageUrl: formData.browserInfo.url,
        pageTitle: formData.browserInfo.pageTitle,
        rateLimitInfo: rateLimitInfo,
      });

      setSubmissionResult({
        success: false,
        message: error.response?.data?.message || error.message || "Failed to submit bug report. Please try again or contact support.",
        rateLimitInfo: rateLimitInfo
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch session info when the dialog opens. Poll every 500 ms (up to 10 attempts)
  // because Datadog's replay recorder can take 1-3 s to initialise in Docker.
  React.useEffect(() => {
    if (!isOpen) return;

    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const maxAttempts = 10;

    const tryFetch = () => {
      const { sessionId, replayUrl } = getDatadogSessionInfo();
      setDatadogSessionId(sessionId);
      setDatadogReplayUrl(replayUrl);
      attempts++;
      if (attempts < maxAttempts && !(sessionId && replayUrl)) {
        timer = setTimeout(tryFetch, 500);
      }
    };

    tryFetch();
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Ensure the session is recorded so investigators can always find the replay
      startDatadogSessionReplay();
    }
    setIsOpen(open);
  };

  const isDescriptionRequired = formData.category
    ? !CATEGORIES_WITHOUT_DESCRIPTION_REQUIRED.includes(formData.category as any)
    : false;
  const canSubmit = !!formData.category && (!isDescriptionRequired || !!formData.description.trim());

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="mt-auto flex px-4 items-center justify-center gap-4 rounded-md bg-red-600 py-2 text-white transition-colors hover:bg-red-400 text-xs"
        >
          <GrAnnounce />
          Report an issue
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Content aria-describedby={undefined} className="fixed right-0 top-0 z-50 h-full w-96 bg-slate-800 shadow-xl border-l border-slate-700">
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <Dialog.Title className="text-xl font-semibold text-white">
                Report an Issue
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="text-slate-400 hover:text-white transition-colors"
                  title="Close dialog"
                >
                  <FaTimes size={20} />
                </button>
              </Dialog.Close>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <form id="bug-report-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-slate-300 mb-2">
                    Category *
                  </label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => handleInputChange("category", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a category...</option>
                    {BUG_REPORT_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="aipxLocation" className="block text-sm font-medium text-slate-300 mb-2">
                    Location *
                  </label>
                  <select
                    id="aipxLocation"
                    value={formData.aipxLocationId || ""}
                    onChange={e => handleInputChange("aipxLocationId", e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={locationList.length === 0}
                  >
                    {locationList.length === 0 ? (
                      <option value="">No locations available</option>
                    ) : (
                      <>
                        <option value="">Select a location...</option>
                        {locationList.map(location => (
                          <option
                            key={location.id}
                            value={location.id}
                          >
                            {location.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-2">
                    {isDescriptionRequired ? "Description *" : "Description (optional)"}
                  </label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder="Please describe the issue in detail, including steps to reproduce if applicable."
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-64 resize-none"
                  />
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                    Session Debug Info
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Session ID
                    </label>
                    <input
                      type="text"
                      disabled
                      readOnly
                      value={datadogSessionId ?? ""}
                      placeholder="Loading..."
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-slate-300 text-xs font-mono cursor-default"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      Session Replay URL
                    </label>
                    <input
                      type="text"
                      disabled
                      readOnly
                      value={datadogReplayUrl ?? ""}
                      placeholder="Loading..."
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-slate-300 text-xs font-mono cursor-default"
                    />
                  </div>
                </div>

                {/* TODO: Screenshot functionality temporarily disabled - Debug why this stalls, with Zoho Support */}
                {/* <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Screenshot (Optional)
                  </label>
                  <div className="space-y-3">
                    {!screenshot ? (
                      <button
                        type="button"
                        onClick={captureScreenshot}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                      >
                        <FaCamera />
                        Take Screenshot
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <div className="relative">
                          <img
                            src={screenshot}
                            alt="Screenshot preview"
                            className="max-w-full h-48 object-contain border border-slate-600 rounded-md"
                          />
                          <button
                            type="button"
                            onClick={removeScreenshot}
                            className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                            title="Remove screenshot"
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={captureScreenshot}
                          className="text-sm text-blue-400 hover:text-blue-300"
                        >
                          Retake Screenshot
                        </button>
                      </div>
                    )}
                  </div>
                </div> */}
            
              </form>
            </div>

            {/* Submission Result Display */}
            {submissionResult && (
              <div className="border-t border-slate-700 p-6">
                <div className={`p-4 rounded-md ${
                  submissionResult.success 
                    ? 'bg-green-900/50 border border-green-700' 
                    : 'bg-red-900/50 border border-red-700'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                      submissionResult.success ? 'bg-green-600' : 'bg-red-600'
                    }`}>
                      {submissionResult.success ? (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-medium ${
                        submissionResult.success ? 'text-green-300' : 'text-red-300'
                      }`}>
                        {submissionResult.success ? 'Success!' : 'Error'}
                      </h4>
                      <p className="text-slate-300 text-sm mt-1">
                        {submissionResult.message}
                      </p>
                      {submissionResult.ticketId && (
                        <p className="text-blue-300 text-sm mt-2 font-mono">
                          Ticket ID: {submissionResult.ticketId}
                        </p>
                      )}
                      {!submissionResult.success && submissionResult.rateLimitInfo && (
                        <div className="mt-3 p-2 bg-yellow-900/30 border border-yellow-700 rounded text-xs">
                          <p className="text-yellow-300 font-medium">Rate Limit Info:</p>
                          <p className="text-yellow-200">
                            Credits remaining: {submissionResult.rateLimitInfo.remaining}
                          </p>
                          {submissionResult.rateLimitInfo.retryAfter && (
                            <p className="text-yellow-200">
                              Retry after: {submissionResult.rateLimitInfo.retryAfter} seconds
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-slate-700 p-6">
              <div className="flex justify-end gap-3">
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="px-4 py-2 text-slate-300 bg-slate-700 rounded-md hover:bg-slate-600 transition-colors"
                  >
                    Cancel
                  </button>
                </Dialog.Close>

                <button
                  type="submit"
                  form="bug-report-form"
                  disabled={isSubmitting || !canSubmit}
                  className={`px-4 py-2 text-white rounded-md transition-all duration-300 ${
                    canSubmit
                      ? 'bg-blue-600 hover:bg-blue-700 transform hover:scale-105 shadow-lg' 
                      : 'bg-slate-600 cursor-not-allowed'
                  } ${isSubmitting ? 'bg-slate-600 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? "Submitting..." : "Submit Report"}
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ZohoBugReportButton;
